import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Wallet, Plus, ArrowUpRight, ArrowDownRight, Smartphone, CreditCard, Building2, Gift, ShieldCheck, TrendingUp, Copy, CheckCircle2, IndianRupee, Sparkles, Loader2,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const STORAGE_BALANCE = "pmr_wallet_balance";
const STORAGE_TXNS = "pmr_wallet_txns";

const QUICK_AMOUNTS = [500, 1000, 2500, 5000, 10000, 25000];

const getInitialBalance = () => {
  const stored = localStorage.getItem(STORAGE_BALANCE);
  return stored ? parseFloat(stored) : 2500;
};

const getInitialTxns = () => {
  const stored = localStorage.getItem(STORAGE_TXNS);
  if (stored) return JSON.parse(stored);
  return [
    { id: "T1004", type: "credit", label: "Welcome bonus", desc: "Sign-up gift", amount: 500, method: "Bonus", date: "2025-06-28" },
    { id: "T1003", type: "credit", label: "Money added", desc: "Via UPI - GPay", amount: 3000, method: "UPI", date: "2025-06-25" },
    { id: "T1002", type: "debit", label: "Bus Booking", desc: "Churu → Delhi (PMRB87654321)", amount: 899, method: "Wallet", date: "2025-06-22" },
    { id: "T1001", type: "credit", label: "Refund", desc: "Cancelled - PMRC55667788", amount: 899, method: "Refund", date: "2025-06-20" },
  ];
};

const METHODS = [
  { id: "upi", label: "UPI", desc: "GPay, PhonePe, Paytm", icon: Smartphone },
  { id: "card", label: "Debit / Credit Card", desc: "Visa, Mastercard, RuPay", icon: CreditCard },
  { id: "netbanking", label: "Net Banking", desc: "All major banks", icon: Building2 },
];

const OFFERS = [
  { title: "Get 10% Extra", desc: "Add ₹5,000 or more and instantly get ₹500 bonus", tag: "PMR500" },
  { title: "Refer & Earn ₹250", desc: "Invite a friend — both of you get ₹250 in wallet", tag: "REFER25" },
  { title: "Wallet-only Discount", desc: "Save 5% on any package paid entirely via wallet", tag: "WALLET5" },
];

const WalletPage = () => {
  const { toast } = useToast();
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    setBalance(getInitialBalance());
    setTxns(getInitialTxns());
  }, []);

  const persist = (newBal, newTxns) => {
    localStorage.setItem(STORAGE_BALANCE, String(newBal));
    localStorage.setItem(STORAGE_TXNS, JSON.stringify(newTxns));
  };

  const addMoney = (e) => {
    e.preventDefault();
    const value = parseFloat(amount);
    if (!value || value < 100) {
      toast({ title: "Enter a valid amount", description: "Minimum add: ₹100" });
      return;
    }
    if (value > 100000) {
      toast({ title: "Amount exceeds daily limit", description: "Max add per transaction: ₹1,00,000" });
      return;
    }
    setProcessing(true);
    setTimeout(() => {
      const bonus = value >= 5000 ? Math.round(value * 0.1) : 0;
      const total = value + bonus;
      const newBal = balance + total;

      const newTxns = [
        {
          id: "T" + Date.now().toString().slice(-6),
          type: "credit",
          label: "Money added",
          desc: `Via ${METHODS.find((m) => m.id === method).label}`,
          amount: value,
          method: METHODS.find((m) => m.id === method).label,
          date: new Date().toISOString().slice(0, 10),
        },
        ...(bonus > 0 ? [{
          id: "T" + (Date.now() + 1).toString().slice(-6),
          type: "credit",
          label: "Bonus credit",
          desc: "10% extra on ₹5,000+",
          amount: bonus,
          method: "Bonus",
          date: new Date().toISOString().slice(0, 10),
        }] : []),
        ...txns,
      ];

      setBalance(newBal);
      setTxns(newTxns);
      persist(newBal, newTxns);
      setAmount("");
      setProcessing(false);
      toast({
        title: `₹${total.toLocaleString("en-IN")} added successfully!`,
        description: bonus > 0 ? `Includes ₹${bonus} bonus. Happy travelling!` : "Money is now available in your wallet.",
      });
    }, 1400);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast({ title: "Code copied!", description: `${code} — apply at checkout.` });
  };

  const totalCredited = txns.filter((t) => t.type === "credit").reduce((s, t) => s + t.amount, 0);
  const totalSpent = txns.filter((t) => t.type === "debit").reduce((s, t) => s + t.amount, 0);

  return (
    <>
      <Header />
      <main>
        <section className="relative pt-40 pb-16 md:pt-48 md:pb-20 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 to-slate-900/60" />
          <div className="relative max-w-7xl mx-auto px-6 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold tracking-wider uppercase">
              <Wallet className="w-3.5 h-3.5" /> PMR Wallet
            </div>
            <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Your travel wallet, always ready
            </h1>
            <p className="mt-3 text-white/85 text-lg max-w-2xl">
              Load money once, book instantly. Earn cashback, avoid checkout hassle, get faster refunds — all with PMR Wallet.
            </p>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
            {/* Balance card */}
            <div className="relative rounded-3xl overflow-hidden p-8 bg-gradient-to-br from-teal-700 via-teal-600 to-teal-500 text-white shadow-2xl">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10" />
              <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-white/5" />
              <div className="relative flex items-start justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/80">
                    <Wallet className="w-3.5 h-3.5" /> PMR Wallet Balance
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <IndianRupee className="w-8 h-8" />
                    <span className="text-5xl md:text-6xl font-bold tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {balance.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="mt-2 text-white/85 text-sm">Available for any booking</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-white/60">PMR Travels</div>
                  <div className="mt-1 font-mono text-xs">**** 3140</div>
                </div>
              </div>

              <div className="relative mt-8 grid grid-cols-2 gap-3">
                <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-wider text-white/70">Total credited</div>
                  <div className="mt-0.5 text-lg font-bold">₹{totalCredited.toLocaleString("en-IN")}</div>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-wider text-white/70">Total spent</div>
                  <div className="mt-0.5 text-lg font-bold">₹{totalSpent.toLocaleString("en-IN")}</div>
                </div>
              </div>
            </div>

            {/* Add money form */}
            <form onSubmit={addMoney} className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-orange-500 text-white flex items-center justify-center">
                  <Plus className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Add money</h3>
                  <p className="text-xs text-slate-500">Add ₹100 to ₹1,00,000 per transaction</p>
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="amt">Amount</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">₹</span>
                  <Input
                    id="amt"
                    type="number"
                    min="100"
                    max="100000"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="1,000"
                    className="pl-7 h-11 text-lg font-semibold"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {QUICK_AMOUNTS.map((a) => (
                    <button
                      type="button"
                      key={a}
                      onClick={() => setAmount(String(a))}
                      className={`px-3 py-1 rounded-md text-xs font-semibold border transition ${
                        String(a) === amount
                          ? "bg-teal-600 text-white border-teal-600"
                          : "bg-white text-slate-700 border-slate-200 hover:border-teal-300"
                      }`}
                    >
                      ₹{a.toLocaleString("en-IN")}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <Label>Payment Method</Label>
                <div className="mt-2 grid grid-cols-1 gap-2">
                  {METHODS.map((m) => {
                    const Icon = m.icon;
                    return (
                      <label
                        key={m.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                          method === m.id ? "border-teal-600 bg-teal-50" : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <input type="radio" name="method" value={m.id} checked={method === m.id} onChange={() => setMethod(m.id)} className="accent-teal-600" />
                        <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-slate-700" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-slate-900">{m.label}</div>
                          <div className="text-[11px] text-slate-500">{m.desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {parseFloat(amount) >= 5000 && (
                <div className="mt-3 rounded-lg bg-orange-50 border border-orange-100 p-3 flex gap-2 text-sm text-slate-700">
                  <Sparkles className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
                  <span>Great! You'll get <strong>₹{Math.round(parseFloat(amount) * 0.1)} extra bonus</strong> on this add.</span>
                </div>
              )}

              <Button type="submit" disabled={processing} className="mt-5 w-full h-12 bg-teal-600 hover:bg-teal-700 text-base">
                {processing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <>Add ₹{amount ? Number(amount).toLocaleString("en-IN") : "0"} to Wallet</>
                )}
              </Button>

              <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-slate-500">
                <ShieldCheck className="w-3 h-3" /> 100% Secure. PCI-DSS · 256-bit encrypted
              </div>
            </form>
          </div>

          {/* Offers */}
          <div className="max-w-7xl mx-auto px-6 mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-5 h-5 text-orange-500" />
              <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Wallet offers</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {OFFERS.map((o) => (
                <div key={o.tag} className="bg-white p-5 rounded-2xl border border-slate-200 hover:border-orange-300 hover:shadow-lg transition">
                  <div className="flex items-start justify-between">
                    <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
                      <Gift className="w-4 h-4" />
                    </div>
                    <Badge variant="outline" className="border-orange-200 text-orange-700 bg-orange-50 font-mono text-[10px]">
                      {o.tag}
                    </Badge>
                  </div>
                  <h4 className="mt-3 font-bold text-slate-900">{o.title}</h4>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{o.desc}</p>
                  <button
                    onClick={() => copyCode(o.tag)}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-orange-500"
                  >
                    <Copy className="w-3 h-3" /> Copy code
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Transactions */}
          <div className="max-w-7xl mx-auto px-6 mt-10">
            <Tabs defaultValue="all">
              <div className="flex items-end justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Transaction History</h3>
                <TabsList className="bg-white border border-slate-200">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="credit">Credits</TabsTrigger>
                  <TabsTrigger value="debit">Debits</TabsTrigger>
                </TabsList>
              </div>

              {["all", "credit", "debit"].map((tab) => {
                const list = tab === "all" ? txns : txns.filter((t) => t.type === tab);
                return (
                  <TabsContent key={tab} value={tab} className="mt-0">
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                      {list.length === 0 && (
                        <div className="p-8 text-center text-sm text-slate-500">No {tab === "all" ? "transactions" : tab + "s"} yet.</div>
                      )}
                      {list.map((t) => (
                        <div key={t.id} className="px-6 py-4 border-b border-slate-100 last:border-b-0 flex items-center gap-4 hover:bg-slate-50">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            t.type === "credit" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          }`}>
                            {t.type === "credit" ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900">{t.label}</div>
                            <div className="text-xs text-slate-500 truncate">{t.desc}</div>
                          </div>
                          <div className="text-right">
                            <div className={`font-bold ${t.type === "credit" ? "text-green-700" : "text-red-600"}`}>
                              {t.type === "credit" ? "+" : "-"}₹{t.amount.toLocaleString("en-IN")}
                            </div>
                            <div className="text-[11px] text-slate-500">{t.method} · {t.date}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>

          <div className="max-w-7xl mx-auto px-6 mt-8">
            <div className="rounded-2xl bg-white p-5 border border-slate-200 flex flex-col md:flex-row items-start md:items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-teal-600" />
                <div>
                  <div className="font-semibold text-slate-900">Ready to book?</div>
                  <div className="text-xs text-slate-500">Use your wallet balance for any booking — flights, hotels, bus, cabs & more.</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button asChild className="bg-teal-600 hover:bg-teal-700"><Link to="/packages">Browse Packages</Link></Button>
                <Button asChild variant="outline"><Link to="/">Book a Ride</Link></Button>
              </div>
            </div>
            <p className="mt-4 text-center text-[11px] text-orange-600 font-semibold">
              DEMO MODE — Wallet is stored locally in your browser (localStorage). No real money is added or spent.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default WalletPage;
